import { ClarkProcessor } from "./ClarkProcessor";
import type { AllFlags } from "../featureFlags";
import type { ClarkProcessorResult } from "./ClarkProcessor";
import type { ClarkAction, ClarkActionFuntions } from "./types";
import type { RouteDef } from "@superblocksteam/shared";
import type { createNameValidator } from "hooks/store/useEntityNameValidator";
import type {
  DataTree,
  DataTreeWidget,
} from "legacy/entities/DataTree/dataTreeFactory";
import type { CanvasWidgetsReduxState } from "legacy/reducers/entityReducers/canvasWidgetsReducer";
import type { WidgetMetadata } from "legacy/reducers/entityReducers/metaReducer";
import type { APP_MODE } from "legacy/reducers/types";
import type { getDynamicLayoutWidgets } from "legacy/selectors/layoutSelectors";
import type { GeneratedTheme } from "legacy/themes";
import type { WidgetProps } from "legacy/widgets/BaseWidget/types";
import type { ApiDtoWithPb } from "store/slices/apisV2/slice";
import type { AppState } from "store/types";

let clarkProcessorInstance: ClarkProcessor | null = null;

export const processActionsIntoChanges = async ({
  clarkRequestId,
  actions,
  dataTree,
  routes,
  theme,
  nameValidator,
  featureFlags,
  widgets,
  apisV2,
  getState,
  focusedEntityId,
  appMode,
  dynamicWidgetLayout,
  widgetsRuntime,
  widgetMetaProps,
  evaluatedWidgets,
  previousAiWidgets,
  organizationId,
  applicationId,
  pageId,
  createComponentFn,
  updateComponentFn,
  deleteComponentFn,
  deleteComponentPropertiesFn,
  renameEntityFn,
  createApiFn,
  updateApiFn,
  addSectionColumnFn,
  createStateVarFn,
  editStateVarFn,
  createTimerFn,
  editTimerFn,
  createEventFn,
  editEventFn,
  moveWidgetFn,
  resizeComponentFn,
}: {
  clarkRequestId: string;
  actions: ClarkAction[];
  dataTree: DataTree;
  routes: RouteDef[];
  theme: GeneratedTheme;
  appMode: APP_MODE;
  dynamicWidgetLayout: ReturnType<typeof getDynamicLayoutWidgets>;
  widgetsRuntime: CanvasWidgetsReduxState;
  widgetMetaProps: WidgetMetadata;
  evaluatedWidgets: Record<string, DataTreeWidget>;
  previousAiWidgets: Record<string, DataTreeWidget>;
  nameValidator: ReturnType<typeof createNameValidator>;
  featureFlags: Partial<AllFlags>;
  widgets: Record<string, Partial<WidgetProps>>;
  apisV2: Record<string, ApiDtoWithPb>;
  getState: () => AppState;
  focusedEntityId: string;
  organizationId: string;
  applicationId: string;
  pageId: string;
} & ClarkActionFuntions): Promise<ClarkProcessorResult> => {
  if (
    !clarkProcessorInstance ||
    clarkProcessorInstance.clarkRequestId !== clarkRequestId
  ) {
    clarkProcessorInstance = new ClarkProcessor(
      {
        clarkRequestId,
        dataTree,
        routes,
        nameValidator,
        theme,
        featureFlags,
        canvasWidgets: widgets,
        apisV2,
        focusedEntityId,
        appMode,
        dynamicWidgetLayout,
        widgetsRuntime,
        widgetMetaProps,
        evaluatedWidgets,
        previousAiWidgets,
        organizationId,
        applicationId,
        pageId,
      },
      getState,
      {
        createComponentFn,
        updateComponentFn,
        deleteComponentFn,
        deleteComponentPropertiesFn,
        renameEntityFn,
        createApiFn,
        updateApiFn,
        addSectionColumnFn,
        createStateVarFn,
        editStateVarFn,
        createTimerFn,
        editTimerFn,
        createEventFn,
        editEventFn,
        moveWidgetFn,
        resizeComponentFn,
      },
    );
  }

  if (!clarkProcessorInstance) {
    throw new Error("No current Clark processor");
  }

  const currentClarkProcessor = clarkProcessorInstance;

  const defaultComponentName = widgets[focusedEntityId]?.widgetName ?? "";

  for (const action of actions) {
    await currentClarkProcessor.addAction(action, defaultComponentName);
  }

  if (!currentClarkProcessor.hasResults()) {
    return {
      widgetChangedKeys: {},
      widgetChanges: {},
      renamesById: {},
      discardedEdits: {},
      metadataByWidgetId: {},
      apiChanges: {},
      changedApiBlockPaths: {},
    } as ClarkProcessorResult;
  }

  currentClarkProcessor.consolidateWidgetChanges();

  return currentClarkProcessor.getResult();
};
